/* eslint-disable @nx/enforce-module-boundaries */
import { Box, Button, HStack, Heading, Tooltip, Stack, Text, Flex, useBreakpointValue } from '@mybridge/ui';
import { useContext, useEffect, useState } from 'react';
import { DiskContext } from '../context';
import { humanFileSize, isDir, isFile, isTeamDir } from 'v4/components/utils';
import { getFullFileName, getUserFullName } from 'v4/lib/commons';
import moment from 'moment';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { DeleteIcon, DeleteIcon2 } from '@mybridge/icons';

export const DiskFileInfoSection = (props) => {
  const { selectedFile, selectedSharedDiskUser, binDisc } = useContext(DiskContext);
  const [showTooltip, setShowTooltip] = useState(true);
  const { t } = useTranslator();
  const capitalize = (str) => {
    if (typeof str !== 'string' || str.trim() === '') {
      return '';
    }

    return str
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getFileName = (file) =>
    isFile(file)
      ? file?.name + '.' + file?.extension
      : isDir(file)
      ? file?.name
      : isTeamDir(file)
      ? file?.team_info?.name
      : file?.name;

  const isMobile = useBreakpointValue({ base: true, md: false });

  // Hide tooltip when modal opens, show when it closes
  useEffect(() => {
    if (binDisc?.isOpen) {
      setShowTooltip(false);
    } else {
      // Delay showing tooltip after modal closes
      const timer = setTimeout(() => {
        setShowTooltip(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [binDisc?.isOpen]);

  if (!selectedFile || !selectedFile?.id) return <></>;

  return (
    <Stack spacing={4} p={isMobile ? 2 : 4} w="100%">
      <Flex
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "flex-start"}
        gap={isMobile ? 4 : 6}
        w="100%"
        flexWrap="wrap"
      >
        <Stack gap={0} maxW={isMobile ? '100%' : '48'}>
          <Heading size="xs" mb={0}>
            <Text maxW={isMobile ? '100%' : '48'} isTruncated color="brandPrimary.500" fontWeight="600" fontSize="md">
              {selectedFile?.type==="RD" ? t(`myDisk.${selectedFile?.name}`) : (selectedFile?.type==='TD' ? selectedFile?.team_info?.name : capitalize(getFullFileName(selectedFile) || ''))}
            </Text>
          </Heading>
          {selectedFile?.total_size || selectedFile?.size ? (
            <Text fontSize="xs" color="gray.600">
              {humanFileSize(
                selectedFile?.total_size ?? selectedFile?.size,
                true
              )}
            </Text>
          ) : (
            ''
          )}
        </Stack>

        <Stack spacing={2} minW={isMobile ? '100%' : 'auto'}>
          <HStack justify="space-between" w="100%">
            <Heading size="xs" fontWeight="500" color="brandPrimary.500">
              {t('myDisk.owner') || "Owner"}
            </Heading>
            <Text fontSize="xs">
              {selectedFile.access_type == 1
                ? getUserFullName(selectedSharedDiskUser)
                : getUserFullName(selectedFile?.owner)}
            </Text>
          </HStack>
          <HStack justify="space-between" w="100%">
            <Heading size="xs" fontWeight="500" color="brandPrimary.500">
              {t('myDisk.changed') || "Changed"}
            </Heading>
            <Text fontSize="xs">
              {moment(selectedFile?.updated_at)?.format?.('MMM DD, YYYY')}
            </Text>
          </HStack>
        </Stack>

        <Stack spacing={2} minW={isMobile ? '100%' : 'auto'}>
          <HStack justify="space-between" w="100%">
            <Heading size="xs" fontWeight="500" color="brandPrimary.500">
              {t('myDisk.downloaded') || "Downloaded"}
            </Heading>
            <Text fontSize="xs">{selectedFile?.download_count === 0 || selectedFile?.download_count == null ? '0 times' : `${selectedFile?.download_count} ${t('myDisk.times') || "times"}`}</Text>
          </HStack>
          <HStack justify="space-between" w="100%">
            <Heading size="xs" fontWeight="500" color="brandPrimary.500">
              {t('myDisk.bin') || "Bin"}
            </Heading>
            <Tooltip 
              label={t('myDisk.bin') || "Bin"}
              isDisabled={!showTooltip}
            >
              <Button
                p={0}
                variant="outline"
                size="sm"
                onClick={binDisc?.onOpen}
                borderColor="transparent"
                h="auto"
                minW="auto"
              >
               <DeleteIcon2 height='15px' width='15px' />
              </Button>
            </Tooltip>
          </HStack>
        </Stack>
      </Flex>
    </Stack>
  );
};
